import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { Business } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';

interface SafeImageProps {
  src?: string | null;
  alt: string;
  sx?: any;
  fallbackIcon?: React.ReactNode;
}

export const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt,
  sx,
  fallbackIcon = <Business fontSize="small" sx={{ color: '#C23535' }} />,
}) => {
  const [imgSrc, setImgSrc] = useState<string | null>(src || null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) {
      setImgSrc(null);
      setHasError(false);
      return;
    }

    let isMounted = true;
    let objectUrlToRevoke: string | null = null;

    if (Capacitor.isNativePlatform() && src.startsWith('http')) {
      fetch(src)
        .then((res) => {
          if (!res.ok) throw new Error('Network error');
          return res.blob();
        })
        .then((blob) => {
          if (isMounted) {
            const objUrl = URL.createObjectURL(blob);
            objectUrlToRevoke = objUrl;
            setImgSrc(objUrl);
            setHasError(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            setImgSrc(src);
          }
        });
    } else {
      setImgSrc(src);
      setHasError(false);
    }

    return () => {
      isMounted = false;
      if (objectUrlToRevoke) {
        URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [src]);

  if (!imgSrc || hasError) {
    return <>{fallbackIcon}</>;
  }

  return (
    <Box
      component="img"
      src={imgSrc}
      alt={alt}
      onError={() => setHasError(true)}
      sx={sx}
    />
  );
};

export default SafeImage;
